import React, { useEffect, useState } from "react";
import { Navigate, useParams, useNavigate } from "react-router-dom";
import { adminListUsers, adminUpdateUser, adminListAlerts, adminMarkAlert, adminLogout, adminGetSettings } from "../lib/api";
import { adminAuth } from "../lib/identity";
import { useAdminAuth } from "../lib/useIdentity";
import { toast } from "sonner";
import { settingsCache } from "../lib/settingsCache";
import { MaliStripe } from "../components/MaliStripe";
import { HeatmapCommandCenter } from "../components/HeatmapCommandCenter";
import { CommandCenterTab } from "../components/admin/CommandCenterTab";
import { BroadcastsTab } from "../components/BroadcastsTab";
import { AgenciesTab } from "../components/AgenciesTab";
import { SovereigntyBanner } from "../components/SovereigntyBanner";
import { CommandHeader } from "../components/CommandHeader";
import { useAdminFirehose } from "../lib/useAdminFirehose";
import { ccBus } from "../lib/commandCenterBus";
import { notificationAudio } from "../lib/notificationSound";
import { AdminAudioSettings } from "../components/AdminAudioSettings";
import { SystemHealthDrawer } from "../components/SystemHealthDrawer";
import { useSystemHealth } from "../lib/useSystemHealth";
import { ExecutiveReportsTab } from "../components/ExecutiveReportsTab";
import { EMERGENCY_FR } from "../i18n.jsx";
import { AdminTabsBar, ADMIN_TABS, ADMIN_TAB_TITLES } from "../components/admin/AdminTabsBar";
import { AdminUsersTab } from "../components/admin/AdminUsersTab";
import { AdminAlertsTab } from "../components/admin/AdminAlertsTab";
import { AreaStatsTab } from "../components/AreaStatsTab";
import { AuditTab } from "../components/admin/AuditTab";
import { SettingsTab } from "../components/admin/SettingsTab";
import { SystemHealthTab } from "../components/admin/SystemHealthTab";
import { PlanificationTab } from "../components/planification/PlanificationTab";
import { PlanificationConfirmDialog } from "../components/planification/PlanificationConfirmDialog";
import { useT } from "../lib/lang";
import { useCountryConfig } from "../lib/useCountryConfig";

const DEFAULT_USERS_FILTER = {
  preset: "all",         // all | today | 7d | 30d | this_month | custom
  customSince: "",       // datetime-local string when preset === custom
  customUntil: "",
  sortBy: "updated_at",  // updated_at | created_at
  sortOrder: "desc",     // desc | asc
};

const computeUsersDateBounds = (f) => {
  const now = new Date();
  let since = null;
  let until = null;
  if (f.preset === "today") {
    since = new Date(now); since.setHours(0, 0, 0, 0);
  } else if (f.preset === "7d") {
    since = new Date(now.getTime() - 7 * 86400000);
  } else if (f.preset === "30d") {
    since = new Date(now.getTime() - 30 * 86400000);
  } else if (f.preset === "this_month") {
    since = new Date(now.getFullYear(), now.getMonth(), 1);
  } else if (f.preset === "custom") {
    if (f.customSince) since = new Date(f.customSince);
    if (f.customUntil) until = new Date(f.customUntil);
  }
  return { since, until };
};

export default function AdminPanelPage() {
  const [token] = useAdminAuth();
  const cfg = useCountryConfig();
  const [t] = useT();
  // Phase 2C (spec §8.2) — Planification reports whether it has
  // unsaved work; AdminTabsBar intercepts a plain-click tab switch
  // while this is true and hands it here as `pendingNavHref` instead of
  // navigating immediately.
  const [hasUnsavedPlanification, setHasUnsavedPlanification] = useState(false);
  const [pendingNavHref, setPendingNavHref] = useState(null);
  const { tab: urlTab } = useParams();
  const navigate = useNavigate();
  // Tab is driven by the URL — `/admin` → dashboard (default),
  // `/admin/alerts` → alerts, etc. Invalid tab names fall back to
  // dashboard so a bookmark with a stale name still renders.
  const tab = (urlTab && ADMIN_TABS.includes(urlTab)) ? urlTab : "dashboard";
  const setTab = (next) => {
    // React Router push — this is what makes Cmd/Ctrl+Click open a
    // new browser tab natively (because <Link>s in AdminTabsBar use
    // real `href` attributes; this fallback is for code paths that
    // navigate programmatically like locateOnMap).
    navigate(next === "dashboard" ? "/admin" : `/admin/${next}`);
  };
  const [users, setUsers] = useState([]);
  const [usersNext, setUsersNext] = useState(null);
  const [usersHasMore, setUsersHasMore] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [alertsNext, setAlertsNext] = useState(null);
  const [alertsHasMore, setAlertsHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [focusAlert, setFocusAlert] = useState(null);
  const [loadingMore, setLoadingMore] = useState({ users: false, alerts: false });
  const [audioSettingsOpen, setAudioSettingsOpen] = useState(false);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [healthDrawerOpen, setHealthDrawerOpen] = useState(false);
  const [usersFilter, setUsersFilter] = useState(DEFAULT_USERS_FILTER);

  const buildUsersParams = (extra = {}) => {
    const p = { limit: 50, ...extra };
    p.sort_by = usersFilter.sortBy;
    p.sort_order = usersFilter.sortOrder;
    const { since, until } = computeUsersDateBounds(usersFilter);
    if (since) p.joined_after = since.toISOString();
    if (until) p.joined_before = until.toISOString();
    return p;
  };

  // System health probe — polled every 30s, surfaces traffic-light in
  // CommandHeader so dispatchers see infra panes before citizens call in.
  const systemHealth = useSystemHealth({ enabled: !!token });

  // Hydrate the global settings cache once on mount + when the user
  // navigates back to /admin/settings (where it may have been edited).
  // The cache backs the sound + WS kill-switches (see useAdminFirehose).
  const [wsEnabled, setWsEnabled] = useState(true);
  useEffect(() => {
    if (!token) return;
    adminGetSettings()
      .then((d) => {
        settingsCache.set(d.settings || {});
        setWsEnabled(!!(d.settings?.admin_ws_enabled !== false));
      })
      .catch(() => { /* fall back to permissive defaults */ });
  }, [token, tab]);

  // Real-time firehose — every new alert from any citizen pushes through
  // WebSocket; plays a sound, adds to `unacked`, badge counter reads off it.
  const firehose = useAdminFirehose({
    enabled: !!token && wsEnabled,
    onAlertCreated: (alert) => {
      setAlerts((prev) => (prev.some((a) => a.id === alert.id) ? prev : [alert, ...prev]));
      toast.success("Nouvelle alerte reçue", {
        description:
          (EMERGENCY_FR[alert.emergency_type]?.label || alert.emergency_type)
          + (alert.reporter_phone ? ` · ${alert.reporter_phone}` : ""),
        duration: 4000,
      });
    },
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const [u, a] = await Promise.all([
        adminListUsers(buildUsersParams()).catch(() => ({ items: [], next_cursor: null, has_more: false })),
        adminListAlerts({ limit: 50 }).catch(() => ({ items: [], next_cursor: null, has_more: false })),
      ]);
      setUsers(u.items || []);
      setUsersNext(u.next_cursor || null);
      setUsersHasMore(!!u.has_more);
      setAlerts(a.items || []);
      setAlertsNext(a.next_cursor || null);
      setAlertsHasMore(!!a.has_more);
    } finally { setLoading(false); }
  };

  const loadMoreUsers = async () => {
    if (!usersNext) return;
    setLoadingMore((s) => ({ ...s, users: true }));
    try {
      const u = await adminListUsers(buildUsersParams({ cursor: usersNext }));
      setUsers((prev) => [...prev, ...(u.items || [])]);
      setUsersNext(u.next_cursor || null);
      setUsersHasMore(!!u.has_more);
    } finally {
      setLoadingMore((s) => ({ ...s, users: false }));
    }
  };

  const loadMoreAlerts = async () => {
    if (!alertsNext) return;
    setLoadingMore((s) => ({ ...s, alerts: true }));
    try {
      const a = await adminListAlerts({ limit: 50, cursor: alertsNext });
      setAlerts((prev) => [...prev, ...(a.items || [])]);
      setAlertsNext(a.next_cursor || null);
      setAlertsHasMore(!!a.has_more);
    } finally {
      setLoadingMore((s) => ({ ...s, alerts: false }));
    }
  };

  useEffect(() => {
    if (!token) return undefined;
    let mounted = true;
    const run = async () => { if (mounted) await refresh(); };
    run();
    return () => { mounted = false; };
  }, [token]);

  // Each tab now lives at its own URL — keep the browser-tab title in
  // sync so a dispatcher with 8 admin windows open can read the OS
  // task-switcher to find the one they want.
  useEffect(() => {
    const title = ADMIN_TAB_TITLES[tab] || "Admin";
    const brand = (cfg?.brand_name || "SOS Mali").toUpperCase();
    document.title = `${title} · ${brand}`;
  }, [tab, cfg?.brand_name]);

  // Iter90 — GPS audit: a post-SOS location refinement landed on an
  // alert already in local state (feeds HeatmapCommandCenter via the
  // `alerts` prop) — patch coordinates/quality/freshness in place so the
  // marker moves without a page refresh. No refetch needed, the
  // firehose message already carries everything.
  useEffect(() => {
    const unsub = ccBus.on("alert.location_updated", (msg) => {
      if (!msg?.alert_id) return;
      setAlerts((prev) => prev.map((x) => (x.id === msg.alert_id ? {
        ...x,
        coordinates: msg.coordinates || x.coordinates,
        location_quality: msg.location_quality,
        location_updated_at: msg.location_updated_at,
      } : x)));
    });
    return () => { unsub && unsub(); };
  }, []);

  if (!token) return <Navigate to="/admin/login" replace />;

  const setStatus = async (phone, status) => {
    try {
      const u = await adminUpdateUser(phone, { status, note: "" });
      setUsers((prev) => prev.map((x) => (x.phone === phone ? u : x)));
      toast.success(`${phone} → ${status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mise à jour impossible");
    }
  };

  const markAlertFalse = async (id, isFalse) => {
    try {
      const a = await adminMarkAlert(id, isFalse);
      setAlerts((prev) => prev.map((x) => (x.id === id ? a : x)));
      // Refresh users so false_alerts counter updates
      const u = await adminListUsers({ limit: 50 });
      setUsers(u.items || []);
      setUsersNext(u.next_cursor || null);
      setUsersHasMore(!!u.has_more);
      toast.success(isFalse ? "Marquée comme fausse" : "Marquée comme légitime");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action impossible");
    }
  };

  const locateOnMap = (a, dotColor, label) => {
    setFocusAlert({
      id: a.id,
      lat: a.coordinates.latitude,
      lng: a.coordinates.longitude,
      type: a.emergency_type,
      color: dotColor,
      label,
      created_at: a.created_at,
      description: a.description || "",
    });
    setTab("map");
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (_e) { /* noop */ }
  };

  const onAlertStatusChanged = (updated) =>
    setAlerts((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));

  return (
    <div className="flex-1 flex flex-col cmd-root min-h-screen" data-testid="admin-panel">
      <MaliStripe />
      <SovereigntyBanner />

      <CommandHeader
        onLogout={async () => { await adminLogout(); adminAuth.clear(); }}
        unackedCount={firehose.unacked.length}
        firehoseLive={firehose.live}
        audioEnabled={notificationAudio.getAdminSettings().enabled}
        audioUnlocked={audioUnlocked || notificationAudio.isUnlocked()}
        onUnlockAudio={async () => {
          const ok = await notificationAudio.unlock();
          if (ok) {
            setAudioUnlocked(true);
            notificationAudio.playAdminAlert();
            toast.success("Son activé");
          } else {
            toast.error("Impossible d'activer le son sur ce navigateur");
          }
          // Same gesture unlocks Web Push — fire and forget.
          try {
            const { subscribeAdmin } = await import("../lib/pushSubscribe");
            subscribeAdmin();
          } catch (e) { void e; }
        }}
        onOpenAudioSettings={async () => {
          if (!notificationAudio.isUnlocked()) {
            const ok = await notificationAudio.unlock();
            if (ok) setAudioUnlocked(true);
          }
          try {
            const { subscribeAdmin } = await import("../lib/pushSubscribe");
            subscribeAdmin();
          } catch (e) { void e; }
          setAudioSettingsOpen(true);
        }}
        systemHealth={systemHealth.data}
        onOpenSystemHealth={() => setHealthDrawerOpen(true)}
      />
      <AdminAudioSettings open={audioSettingsOpen} onClose={() => setAudioSettingsOpen(false)} />
      <SystemHealthDrawer
        open={healthDrawerOpen}
        onClose={() => setHealthDrawerOpen(false)}
        data={systemHealth.data}
        loading={systemHealth.loading}
        error={systemHealth.error}
        onRefresh={systemHealth.refresh}
      />

      <AdminTabsBar
        tab={tab}
        usersCount={users.length}
        usersHasMore={usersHasMore}
        alertsCount={alerts.length}
        alertsHasMore={alertsHasMore}
        guardTab={hasUnsavedPlanification ? "planning" : null}
        onGuardedNavigate={setPendingNavHref}
      />
      {pendingNavHref && (
        <PlanificationConfirmDialog
          testId="planification-unsaved-nav-confirm"
          title={t.planificationUnsavedNavTitle}
          message={t.planificationUnsavedNavMessage}
          confirmLabel={t.planificationLeaveAndDiscard}
          cancelLabel={t.planificationStayHere}
          danger
          onConfirm={() => { const href = pendingNavHref; setPendingNavHref(null); navigate(href); }}
          onCancel={() => setPendingNavHref(null)}
        />
      )}

      {loading && (
        <div className="px-5 py-6 text-xs text-zinc-500 font-mono tracking-widest uppercase flex items-center gap-2">
          <span className="cmd-pulse-dot w-1.5 h-1.5 rounded-full bg-emerald-400" />
          Chargement des données opérationnelles…
        </div>
      )}

      {!loading && tab === "users" && (
        <AdminUsersTab
          users={users}
          usersHasMore={usersHasMore}
          usersFilter={usersFilter}
          onFiltersChange={setUsersFilter}
          onApplyFilters={refresh}
          onResetFilters={() => {
            setUsersFilter(DEFAULT_USERS_FILTER);
            setTimeout(refresh, 0);
          }}
          onSetStatus={setStatus}
          onLoadMore={loadMoreUsers}
          loadingMore={loadingMore.users}
          token={token}
        />
      )}

      {!loading && tab === "alerts" && (
        <AdminAlertsTab
          alerts={alerts}
          alertsHasMore={alertsHasMore}
          unacked={firehose.unacked}
          onAckAlert={firehose.ackAlert}
          onMarkFalse={markAlertFalse}
          onLocate={locateOnMap}
          onLoadMore={loadMoreAlerts}
          loadingMore={loadingMore.alerts}
          onStatusChanged={onAlertStatusChanged}
          token={token}
        />
      )}

      {!loading && tab === "dashboard" && <CommandCenterTab />}
      {!loading && tab === "reports" && <ExecutiveReportsTab />}
      {!loading && tab === "map" && (
        <HeatmapCommandCenter
          focusAlert={focusAlert}
          onFocusConsumed={() => setFocusAlert(null)}
          flashingAlerts={firehose.unacked}
          onAckAlert={firehose.ackAlert}
        />
      )}
      {!loading && tab === "planning" && <PlanificationTab onUnsavedChange={setHasUnsavedPlanification} />}
      {!loading && tab === "broadcasts" && <BroadcastsTab />}
      {!loading && tab === "agencies" && <AgenciesTab />}
      {!loading && tab === "areas" && <AreaStatsTab />}
      {!loading && tab === "audit" && <AuditTab />}
      {!loading && tab === "settings" && <SettingsTab />}
      {!loading && tab === "system" && <SystemHealthTab />}
    </div>
  );
}
